import { FormField } from '@/components/ui/Form';
import { Form, FormInput, FormItem, FormLabel, MultiSelect, Select } from '@/components/wm';
import { getOptionsFromJson, getStoreResource } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import debounce from 'lodash/debounce';
import React, { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export function Properties({ data, onChangeField, onCompleted }) {
    const typeOptions = [
        { value: 'instantaneous', label: i18next.t('glossary.instantaneous').capitalize() },
        { value: 'average', label: i18next.t('glossary.average').capitalize() },
        { value: 'cumulative', label: i18next.t('glossary.cumulative').capitalize() },
        ...(WiseMetering.currentUser.isSuperUser() ? [{ value: 'baseline', label: i18next.t('glossary.baseline').capitalize() }] : [])
    ];

    const formSchema = useMemo(() => z.object({
        name: z.string().optional(),
        type: z.string()
            .refine(val => typeOptions.map(opt => opt.value).includes(val), {
                message: 'Invalid type selected'
            })
            .default(typeOptions[0].value),
        userIds: z.array(z.string()).optional()
    }).superRefine((data, ctx) => {
        if (data.type !== 'baseline' && (!data.name || data.name.trim() === '')) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Name is required',
                path: ['name']
            });
        }
    }), [typeOptions]);

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: data.name || '',
            type: data.type || typeOptions[0].value,
            userIds: data.userIds || []
        },
        mode: 'onBlur'
    });

    const type = form.watch('type');
    const isBaseline = type === 'baseline';

    const debouncedTriggers = useRef({
        name: debounce(() => form.trigger('name'), 600)
    });

    const resetFields = value => {
        if (onChangeField) {
            onChangeField('type')(value);

            form.setValue('indicatorIds', []);
            onChangeField('indicatorIds')([]);

            form.setValue('indicatorKind', '');
            onChangeField('indicatorKind')('');
        }
    };

    const userOptions = useMemo(() => {
        const users = getStoreResource('users') || [];
        return getOptionsFromJson(users);
    }, []);

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid]);

    const isFirstRun = useRef(true);
    // Logic for baseline type: clear and disable name
    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }

        if (isBaseline) {
            form.setValue('name', '');
            if (onChangeField) {
                onChangeField('name')('');
            }
            form.clearErrors('name');
        }
        form.trigger();
    }, [isBaseline]);

    useEffect(() => {
        return () => {
            Object.values(debouncedTriggers.current).forEach(fn => fn.cancel());
        };
    }, []);

    return (
        <Form {...form}>
            <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                    <FormItem itemInfo={
                        field.value === 'cumulative'
                            ? i18next.t('alarms.cumulative_description').capitalize()
                            : null}
                    >
                        <FormLabel>{i18next.t('common.type').capitalize()}</FormLabel>
                        <Select
                            onChange={value => {
                                field.onChange(value);
                                resetFields(value);
                            }}
                            options={typeOptions}
                            value={field.value}
                        />
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel className={isBaseline && 'tw-opacity-50'}>
                            {i18next.t('common.name').capitalize()}
                        </FormLabel>
                        <FormInput
                            {...field}
                            disabled={isBaseline}
                            onChange={e => {
                                const val = e.target.value;

                                field.onChange(val);
                                if (onChangeField) {
                                    onChangeField('name')(val);
                                }
                            }}
                            placeholder={isBaseline ? i18next.t('alarms.auto_generated').capitalize() : ''}
                        />
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="userIds"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{i18next.t('common.notifications').capitalize()}</FormLabel>
                        <MultiSelect
                            defaultValue={field.value}
                            onValueChange={vals => {
                                field.onChange(vals);
                                if (onChangeField) {
                                    onChangeField('userIds')(vals);
                                }
                            }}
                            options={userOptions}
                            variant="secondary"
                        />
                    </FormItem>
                )}
            />
        </Form>
    );
}
